import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { doNothing } from "@examspark/common";
import {
  Button,
  HSpace,
  useVolatileCallback,
  VSpace,
} from "@examspark/common-ui";
import { IconNotepadLine } from "@examspark/common-ui/dist/components/icons";
import {
  ObjectiveCategoryBadge,
  getObjectiveCategory,
} from "@examspark/customer/tcmpc";
import { Objective } from "@examspark/types";

import ObjectiveBadge from "../common/ObjectiveBadge";
import ObjectiveSpecificity from "../common/ObjectiveSpecificity";
import UnitContent from "../common/UnitContent";
import ObjectiveDetailModal from "../ObjectiveDetailModal";

const Notes = styled(UnitContent)`
  background-color: #ece8f6;
  padding: 1rem;
  font-size: 1.15rem;
  line-height: 1.55em;
  color: #444;

  img {
    max-width: 100%;
  }
`;

type Props = {
  objective?: Objective | null;
  text?: string | null;
  showAllNotes?: boolean;
  showNotes?: boolean;
  showAnnotations?: {
    [key: string]: boolean;
  };
  onShowNotesChange?: (showNotes: boolean) => void;
  onObjectiveBadgeClick?: () => void;
  className?: string;
};

const AlignmentViewObjective = ({
  objective,
  text,
  showAllNotes,
  showNotes,
  showAnnotations,
  onShowNotesChange,
  onObjectiveBadgeClick,
  className,
}: Props) => {
  // Deliberately don't want this to trigger if the callback reference changes
  const handleNotesChange = useVolatileCallback(onShowNotesChange ?? doNothing);
  useEffect(() => {
    handleNotesChange(!!showAllNotes);
  }, [showAllNotes, handleNotesChange]);

  const [showModal, setShowModal] = useState(false);

  onObjectiveBadgeClick = onObjectiveBadgeClick ?? (() => setShowModal(true));

  return (
    <div className={className}>
      {objective != null && (
        <ObjectiveDetailModal.Single
          objective={objective}
          show={showModal}
          onShowChange={setShowModal}
        />
      )}
      <VSpace size="0.5rem">
        {objective?.abbreviation && (
          <div style={{ display: "flex" }}>
            <HSpace
              style={{
                flex: "1",
                alignItems: "center",
                gap: "0.8rem",
                display: "flex",
              }}
            >
              <ObjectiveBadge
                objective={objective}
                onClick={onObjectiveBadgeClick}
              />
              <HSpace
                style={{
                  alignItems: "center",
                  gap: "0.3rem",
                }}
              >
                <ObjectiveCategoryBadge
                  value={getObjectiveCategory(objective)}
                />
                {!!objective?.notes && onShowNotesChange != null && (
                  <Button
                    size="sm"
                    variant="plain"
                    title="Toggle Notes"
                    onClick={() => onShowNotesChange(!showNotes)}
                  >
                    <IconNotepadLine width="16" />
                  </Button>
                )}
              </HSpace>
            </HSpace>
          </div>
        )}
        <div>{objective?.content}</div>
        {!!text && <div>{text}</div>}
        {/* 
          If they pass showAnotations, we want the 'full' labeled specificity
          view instead of the slim notes-only view
        */}
        {showAnnotations == null ? (
          <>
            {showNotes && !!objective?.notes && (
              <Notes
                content={objective.notes}
                options={{ marks: { tecd: "exclude" } }}
              />
            )}
          </>
        ) : (
          <>
            {showNotes &&
              (!!objective?.notes || !!objective?.annotations?.length) && (
                <ObjectiveSpecificity
                  objective={objective}
                  showAnnotationNotes={showAnnotations?.["tcmpc.notes"]}
                  showAnnotationCcrs={showAnnotations?.["tcmpc.ccrs"]}
                  showAnnotationRcfp={showAnnotations?.["tcmpc.rcfp"]}
                />
              )}
          </>
        )}
      </VSpace>
    </div>
  );
};

export default React.memo(AlignmentViewObjective);
